// client/src/components/auth/SetPassword.tsx (TypeScript since ST-00216; behaviour unchanged)
//
// Two modes:
//  - token mode  (?token=...): invite / forgot-password reset -> POST /auth/set-password
//  - change mode (no token):   forced change after first login -> POST /auth/change-password
import {
  useState,
  useRef,
  useMemo,
  type ComponentType,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, CheckCircle } from 'lucide-react';
import PasswordInputJs from '../common/PasswordInput';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AuthBackground, generateParticles } from './Login';
import type { HttpErrorLike, PasswordInputProps } from '../../types/clientShell';
import './Login.css';

// PasswordInput.jsx is still JavaScript (see Login.tsx).
const PasswordInput = PasswordInputJs as unknown as ComponentType<PasswordInputProps>;

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const MIN_LEN = 12;

const SetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const tokenMode = !!token;
  const { logout } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const particles = useMemo(() => generateParticles(20), []);
  const mazeRef = useRef<SVGSVGElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      if (tokenMode) {
        await api.auth.setPassword(token, password);
        setDone(true);
      } else {
        await api.auth.changePassword(currentPassword, password);
        // Stays signed in (the server reissues the session). A full load so the
        // app re-reads the session: the forced-change state (and the screens it
        // kept closed) is gone only after /auth/me says so (ST-00215, IAM-14).
        window.location.assign('/');
      }
    } catch (err) {
      setError(
        (err as HttpErrorLike).response?.data?.message ||
          'Could not set your password. The link may have expired.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <AuthBackground particles={particles} mazeRef={mazeRef} particlesRef={particlesRef} />
      <div className="login-card" style={{ opacity: 1, transform: 'none' }}>
        <div className="login-logo">
          <span className="login-wordmark" role="img" aria-label="LexiBridge">
            <span className="lw-lexi">Lexi</span>
            <span className="lw-divider" />
            <span className="lw-bridge">Bridge</span>
          </span>
        </div>
        <h2 className="login-welcome">
          {tokenMode ? 'Set your password' : 'Choose a new password'}
        </h2>

        {done ? (
          <>
            <p className="login-prompt">
              <CheckCircle size={16} style={{ verticalAlign: 'middle' }} /> Your password has been
              set. You can now sign in.
            </p>
            {/* A full load: any session this tab still held was revoked by the
                reset, and the app must re-read that before showing sign-in. */}
            <button className="login-button" onClick={() => window.location.assign('/login')}>
              Go to sign in
            </button>
          </>
        ) : (
          <form
            onSubmit={submit}
            style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}
          >
            <p className="login-prompt">
              {tokenMode
                ? 'Create a password for your account.'
                : 'For security, please set a new password to continue.'}
            </p>
            {!tokenMode && (
              <PasswordInput
                tone="dark"
                placeholder="Current password"
                aria-label="Current password"
                value={currentPassword}
                autoComplete="current-password"
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={submitting}
                style={inputStyle}
              />
            )}
            <PasswordInput
              tone="dark"
              placeholder={`New password (min ${MIN_LEN} chars)`}
              aria-label="New password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
              style={inputStyle}
            />
            <PasswordInput
              tone="dark"
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
              required
              disabled={submitting}
              style={inputStyle}
            />
            {error && (
              <p role="alert" style={{ color: '#ff6b6b', fontSize: '0.85rem', margin: 0 }}>
                {error}
              </p>
            )}
            <button type="submit" className="login-button" disabled={submitting}>
              <KeyRound size={18} /> {submitting ? 'Saving…' : 'Set password'}
            </button>
            {!tokenMode && (
              // A way out of the forced change (ST-00215 review 2, S2): every other
              // screen leads back here until the password is changed, so offer the
              // reset e-mail (temporary password forgotten) and signing out (wrong
              // account, shared computer).
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  fontSize: '0.85rem',
                  marginTop: '4px',
                }}
              >
                <a href="/forgot-password" style={{ color: '#45B7AA', textDecoration: 'none' }}>
                  Forgot your password?
                </a>
                <button
                  type="button"
                  onClick={logout}
                  disabled={submitting}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#45B7AA',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
